<i18n src='../common_locales.yaml'></i18n>
<i18n>
en:
  back_to_login: "Back to login"
  dont_have_account: "Don't have an account?"
  email_address: "Email address"
  email_invalid: "Invalid email address"
  email_required: "Email is required"
  forgot_password: "Forgot password"
  login_error_fb: "Your account was created using Facebook. Please login using Facebook"
  login_failed: "Login failed"
  login_help: " Use the same account as Halal Navi app"
  login_with: "Login with "

  password_required: "Password is required"
  password_reset_description: "Please enter your email address and we will send you an email on how to reset your password."
  password_reset_error-1: "No user for given email"
  password: "Password"
  proceed_with_facebook: "Login with Facebook"
  register_here: "Register here"
  reset_password: "Reset Password"

ja:
  back_to_login: "ログインして戻る"
  dont_have_account: "アカウントを持っていない？"
  email_address: "電子メールアドレス"
  email_invalid: "無効なメールアドレス"
  email_required: "メールが必要"
  forgot_password: "パスワードをお忘れですか"
  login_error_fb: "Please ログイン using Facebook"
  login_failed: "ログインに失敗しました"
  login_help: " Use the same account as Halal Navi app"
  login_with: "ログイン "
  password_required: "パスワードが必要"
  password_reset_description: "メールアドレスを入力してください。パスワードをリセットする方法のメールが届きます。"
  password_reset_error-1: "No user for given email"
  password: "パスワード"
  proceed_with_facebook: "Facebook ログイン"
  register_here: "ここに登録する"
  reset_password: "パスワードを再設定する"
</i18n>

<template lang="pug">
transition(name="modal")
  .modal-mask
    .modal-wrapper.modal.mt-5(@click.self="disableclose ? '' : $modal.close()")
      .modal-dialog(role="document")
        .modal-content(v-if="!showForgotPasswordView")
          .modal-header
            h3.modal-title.mr-1 {{$t('login_with')}}
              span.text-theme-green Halal Navi ID
            button.close(v-if="!disableclose" type="button" @click="$modal.close()")
              span(aria-hidden="true") &times;
          .modal-body
            p.modal-title.text-muted.mb-3 {{$t('login_help')}}
            .alert.alert-danger(role="alert"  v-if="errorMessage")
              | {{$t('login_failed')}}
              br
              small {{$t(errorMessage)}}
            form
              .form-group
                label {{$t('email_address')}}
                input.form-control(type="email" v-model="loginEmail" @blur="$v.loginEmail.$touch()" :class="{'is-invalid': $v.loginEmail.$error}" required)
                .invalid-feedback(v-if="!$v.loginEmail.required") {{$t('email_required')}}
                .invalid-feedback(v-if="!$v.loginEmail.email") {{$t('email_invalid')}}
              .form-group.mb-2
                label {{$t('password')}}
                input.form-control(type="password" v-model="loginPassword" @keyup.enter="login" @blur="$v.loginPassword.$touch()" :class="{'is-invalid': $v.loginPassword.$error}" required)
                .invalid-feedback(v-if="!$v.loginPassword.required") {{$t('password_required')}}
              a.text-14(v-on:click.stop.prevent="forgotPasswordPressed" href="")  {{$t('forgot_password')}}?
              button.mt-3.btn.btn-primary.btn-block(v-on:click.prevent="loginButtonPressed" :class="{ 'is-loading': loginLoading }") {{$t('login')}}
            p.text-center.text-14.text-secondary.mt-4.mb-2 Or
            button.mt-3.btn.btn-primary.btn-block.fb-signin-button(@click="fbLoginButtonPressed" :class="{ 'is-loading': facebookLoading }") <span class="fb-icon"></span>{{$t('proceed_with_facebook')}}
          .modal-footer
            p.text-14.mb-0 {{$t('dont_have_account')}}
            router-link.text-14(@click.native="$modal.close()" :to="next ? `/register?next=${next}` : '/register'" active-class="active" data-dismiss="modal" exact) {{$t('register_here')}}
        .modal-content(v-else)
          .modal-header
            h3.modal-title {{$t('forgot_password')}}
            button.close(@click="$modal.close()" type="button")
              span(aria-hidden="true") &times;
          .modal-body
            div(v-if="successResetPassword")
              .alert.alert-success(role="alert")
                strong Success!
                |  {{ successResetPassword }}
            div(v-else)
              p.text-secondary.text-14 {{$t('password_reset_description')}}
              .alert.alert-danger(role="alert"  v-if="errorResetPassword") {{ $t(errorResetPassword) }}
              form
                .form-group
                  label {{$t('email_address')}}
                  input.form-control(type="email" v-model="resetEmail" @keyup.enter="forgotPasswordButtonPressed")
            a.text-14(v-on:click.stop.prevent="backToLoginPressed" href="") &#8592; {{$t('back_to_login')}}
          .modal-footer(v-if="!successResetPassword")
            button.btn.btn-primary.btn-block(type="button" v-on:click="forgotPasswordButtonPressed" :class="{ 'is-loading': resetPasswordLoading }") {{$t('reset_password')}}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'

import api from '../api'

export default {
  name: 'login-view',
  props: {
    next: {
      default: null
    },
    callback: {
      default: null
    },
    disableclose: {
      default: false
    }
  },
  data () {
    return {
      loginEmail: '',
      loginPassword: '',
      errorMessage: '',
      showForgotPasswordView: false,
      resetEmail: '',
      successResetPassword: '',
      errorResetPassword: '',
      loginLoading: false,
      facebookLoading: false,
      resetPasswordLoading: false
    }
  },
  validations: {
    loginEmail: { required, email },
    loginPassword: { required }
  },
  computed: mapGetters({
    user: 'getUser'
  }),
  methods: {
    ...mapActions([
      'login',
      'setUser'
    ]),
    forgotPasswordPressed () {
      this.showForgotPasswordView = true
    },
    backToLoginPressed () {
      this.successResetPassword = ''
      this.resetEmail = ''
      this.errorResetPassword = ''
      this.showForgotPasswordView = false
    },
    loginButtonPressed () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      this.errorMessage = ''
      this.loginLoading = true
      var data = { email: this.loginEmail, password: this.loginPassword, login_method: 'email' }
      api.post('/customers/login/', data)
        .then(response => {
          response.data.user.email = this.loginEmail
          this.setUser(response.data.user)
          this.login(response.data.token)
          this.email = ''
          this.password = ''
          this.loginFinished()
        })
        .catch(error => {
          if (error.response) {
            console.log(error.response.data)
            var errorData = error.response.data.error
            if (errorData === 'cannot logged in by email') {
              this.errorMessage = 'login_error_fb'
            } else if (errorData === 'wrong password') {
              this.errorMessage = 'Wrong password'
            } else if (errorData === 'user not exists') {
              this.errorMessage = 'User not exists, please sign up for a new account'
            } else {
              this.errorMessage = 'Something when wrong, please try again'
            }
          } else {
            console.log('Error', error.message)
            this.errorMessage = error.message
          }
          this.loginLoading = false
        })
    },
    fbLoginButtonPressed () {
      var self = this
      self.errorMessage = ''
      self.facebookLoading = true
      window.FB.login(response => {
        self.facebookLoading = false
        var status = response.status
        if (!status || status === 'unknown') {
          self.$toasted.error('Facebook login cancelled')
          return
        }
        if (status === 'not_authorized') {
          self.$toasted.error('Facebook account was not authorized')
          return
        }
        if (status === 'connected') {
          window.FB.api('/me', {'fields': 'email, id'}, function (response) {
            if (!response || response.error) {
              self.$toasted.error('Login with Facebook error')
            } else {
              self.submitFacebookLoginData(response.id, response.email)
            }
          })
        }
      }, {scope: 'email,public_profile', auth_type: 'rerequest'})
    },
    submitFacebookLoginData (fbID, fbEmail) {
      if (!fbID || !fbEmail) {
        self.$toasted.error('Email information needed! Please grant Facebook permission for your email address.')
        return
      }
      var data = { facebook_id: fbID, email: fbEmail, login_method: 'facebook' }
      api.post('/customers/login/', data)
        .then(response => {
          response.data.user.email = fbEmail
          this.setUser(response.data.user)
          this.login(response.data.token)
          this.facebookLoading = false
          this.loginFinished()
        })
        .catch(error => {
          if (error.response) {
            console.log(error.response.data.error)
            if (error.response.data.error.indexOf('already exists') !== -1) {
              this.errorMessage = 'Your Account previously registered using Email. Please Login using Email.'
            } else {
              this.errorMessage = 'Unable to login with your Facebook account.'
            }
          } else {
            this.errorMessage = error.message
          }
        })
    },
    forgotPasswordButtonPressed () {
      var data = {
        email: this.resetEmail
      }
      api.post('/customers/password_reset/', data)
        .then(response => {
          this.successResetPassword = 'Please check your email and follow the instruction to reset your password.'
        })
        .catch(error => {
          this.loginFailed()
          if (error.response) {
            console.log(error.response.data)
            var errorData = error.response.data.error
            if (errorData === 'user not exists') {
              this.errorResetPassword = 'password_reset_error-1'
            } else {
              this.errorResetPassword = errorData
            }
          } else {
            console.log('Error', error.message)
            this.errorResetPassword = error.message
          }
        })
    },
    loginFinished () {
      this.$modal.close()
      if (this.next) {
        if (this.$route.path === this.next) {
          location.reload()
        }
        this.$router.push(this.next)
      }
      if (this.callback) {
        console.log('has callback')
        this.callback()
      }
      this.loginLoading = false
      this.$ga.set('userId', this.user.profile.hn_id.toString())
    }
  }
}
</script>

<style lang="scss" scoped>
.fb-signin-button {
  display: inline-block;
  background-color: #4267b2;
  color: #fff;
}
.fb-icon {
  background-image: url('.././assets/button_fb_logo.svg');
  background-repeat: no-repeat;
  height: 16px;
  width: 16px;
  display: inline-block;
  margin-right: 1rem;
  margin-bottom: -2px;
  position: relative;
}
</style>



// WEBPACK FOOTER //
// src/views/LoginView.vue